// Split a Report It attachment into the fixed multipart plan the backend signs
// (MV-1077 §8.2).
//
// The backend mints one presigned UploadPart URL per part and binds each
// signature to that part's SHA-256, so the split and the digests must be known
// before POST /reports — the client cannot renegotiate them later. Digesting
// goes through crypto.subtle, which hashes off the main thread, so a 100 MB
// screen recording does not freeze the dialog while it is prepared.

export interface AttachmentPartDeclaration {
  partNumber: number;
  byteSize: number;
  checksumSha256Base64: string;
}

export interface PartPlanEntry {
  partNumber: number;
  start: number;
  end: number;
}

export interface ComputePartsDeps {
  /** Injected for tests; defaults to the platform SHA-256 implementation. */
  digestSha256?: (bytes: ArrayBuffer) => Promise<ArrayBuffer>;
  /** Called after each part is digested, so the UI can show real progress. */
  onPartDigested?: (digested: number, total: number) => void;
}

export const DEFAULT_PART_BYTES = 8 * 1024 * 1024;

/**
 * The byte ranges of each part. Parts are consecutive and equal-sized except
 * the last, which holds the remainder; a file smaller than one part is a single
 * final part, and an empty file still yields one part so the plans agree.
 */
export function planParts(byteSize: number, partBytes: number): PartPlanEntry[] {
  const size = Math.max(0, Math.floor(byteSize));
  const chunk = Math.max(1, Math.floor(partBytes));
  const plan: PartPlanEntry[] = [];

  let start = 0;

  while (start < size) {
    const end = Math.min(start + chunk, size);
    plan.push({ partNumber: plan.length + 1, start, end });
    start = end;
  }

  if (plan.length === 0) {
    plan.push({ partNumber: 1, start: 0, end: 0 });
  }

  return plan;
}

const base64FromBytes = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
};

const platformDigest = async (bytes: ArrayBuffer): Promise<ArrayBuffer> => {
  const subtle = globalThis.crypto?.subtle;

  if (!subtle) {
    throw new Error('SHA-256 is unavailable in this browser');
  }

  return subtle.digest('SHA-256', bytes);
};

/**
 * Digest each part of a file in order. Throws when the platform has no
 * SubtleCrypto (non-secure context), which the caller surfaces as an
 * attachments-unavailable state rather than sending an unsignable declaration.
 */
export async function computeAttachmentParts(
  file: Blob,
  partBytes: number = DEFAULT_PART_BYTES,
  deps: ComputePartsDeps = {},
): Promise<AttachmentPartDeclaration[]> {
  const digest = deps.digestSha256 ?? platformDigest;
  const plan = planParts(file.size, partBytes);
  const parts: AttachmentPartDeclaration[] = [];

  for (let i = 0; i < plan.length; i += 1) {
    const entry = plan[i];
    // Sequential on purpose: hashing every part of a 100 MB recording at once
    // would hold the whole file in memory alongside the File itself.
    // eslint-disable-next-line no-await-in-loop
    const bytes = await file.slice(entry.start, entry.end).arrayBuffer();
    // eslint-disable-next-line no-await-in-loop
    const hash = await digest(bytes);

    parts.push({
      partNumber: entry.partNumber,
      byteSize: entry.end - entry.start,
      checksumSha256Base64: base64FromBytes(hash),
    });

    deps.onPartDigested?.(parts.length, plan.length);
  }

  return parts;
}
